/**
 * Deep Link Event Listener
 *
 * Listens for navigation and sync events dispatched from native code
 * (iOS AppDelegate / Android MainActivity) and routes them to the app.
 */

interface DeepLinkNavigateEvent extends CustomEvent {
  detail: {
    route: string;
  };
}

type NavigateCallback = (route: string) => void;
type SyncCallback = () => void;

let navigateCallback: NavigateCallback | null = null;
let syncCallback: SyncCallback | null = null;

/**
 * Initialize deep link listener
 * Call this once when the app starts
 */
export function initDeepLinkListener(
  onNavigate: NavigateCallback,
  onSync: SyncCallback
): () => void {
  navigateCallback = onNavigate;
  syncCallback = onSync;

  // Navigation event from native
  const handleNavigate = (event: Event) => {
    const customEvent = event as DeepLinkNavigateEvent;
    const route = customEvent.detail?.route;
    if (route && navigateCallback) {
      console.log('[DeepLink] Navigate to:', route);
      navigateCallback(route);
    }
  };

  // Sync event from native
  const handleSync = () => {
    if (syncCallback) {
      console.log('[DeepLink] Trigger sync');
      syncCallback();
    }
  };

  window.addEventListener('promptnote:navigate', handleNavigate);
  window.addEventListener('promptnote:sync', handleSync);

  // Return cleanup function
  return () => {
    window.removeEventListener('promptnote:navigate', handleNavigate);
    window.removeEventListener('promptnote:sync', handleSync);
    navigateCallback = null;
    syncCallback = null;
  };
}

/**
 * Parse a route string into path and query params
 */
export function parseRoute(route: string): {
  path: string;
  params: Record<string, string>;
} {
  const [path, queryString] = route.split('?');
  const params: Record<string, string> = {};

  if (queryString) {
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }

  return { path, params };
}

/**
 * Navigate to a deep link route
 * This can be used to programmatically trigger navigation
 */
export function navigateToRoute(route: string): void {
  window.dispatchEvent(
    new CustomEvent('promptnote:navigate', {
      detail: { route },
    })
  );
}

/**
 * Trigger a sync
 * This can be used to programmatically trigger sync
 */
export function triggerSync(): void {
  window.dispatchEvent(new CustomEvent('promptnote:sync'));
}
